/**
 * Holds the browser's deferred install prompt.
 *
 * Chromium fires `beforeinstallprompt` once, early — often before React has
 * mounted. Capturing starts from main.tsx for that reason; the event is held
 * here until a component asks for it, and cleared once the app is installed.
 */
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

let held: BeforeInstallPromptEvent | null = null;
let started = false;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

export function startCapturingInstallPrompt(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  window.addEventListener('beforeinstallprompt', (event) => {
    // Stops the browser's own mini-infobar; we show the prompt from our banner.
    event.preventDefault();
    held = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    held = null;
    notify();
  });
}

export function subscribeInstallPrompt(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

export function getInstallPrompt(): BeforeInstallPromptEvent | null {
  return held;
}

/** After the prompt has been shown: a deferred event can be used once only. */
export function clearInstallPrompt(): void {
  if (held === null) return;
  held = null;
  notify();
}
